import { createStore, type StoreApi } from "zustand/vanilla";
import { formatText } from "../i18n/texts";
import { showDialog } from "../ui/dialog";
import { ContactDirectoryClient, ContactRequestFailure } from "./contactDirectoryClient";
import {
  automaticFields,
  batchSized,
  clearedQuery,
  contactDate,
  contactFields,
  creationDated,
  fullName,
  isDateColumn,
  mask,
  matches,
  mobileBatchSize,
  normalSort,
  parseContactQuery,
  queryKey,
  readAllTags,
  sameQuery,
  sortColumn,
  sortDescending,
  sortField,
  tagReturnUrl,
  toggleSort,
  type ContactDetailResult,
  type ContactPageResult,
  type ContactQuery,
  type ContactRights,
  type ContactRow,
  type ContactSession,
  type TagCatalogRow,
} from "./contactModel";
import { clearViewState, readStored, restore, setViewOwner, storeView, viewState, viewUrl } from "./contactViewState";
import { openContactConversation, openContactSession, revalidateContactScope } from "./dock";
import { TagCatalogClient } from "./tagCatalogClient";
import { knownTagColor } from "./tagColor";
import * as dom from "./workspaceDom";

// El espacio de Contactos (ContactWorkspace.razor.cs del portal actual) como un controlador: los mismos campos, los
// mismos pasos y las mismas defensas (revisión, cancelación, sesión, empresa y alcance). Lo que se dibuja sale de
// `view` (una copia inmutable que se publica en cada cambio, como StateHasChanged); lo demás vive aquí.

export type ImportWizardHandle = {
  open(): void;
  invalidate(message?: string): void;
  hasPreparation(): boolean;
  preservePreparation(): void;
  restorePreparation(): Promise<boolean>;
  restore(id: string): Promise<void>;
  markClosed(): void;
};

export type EditorHandle = { confirmLeave(): Promise<boolean> };

export type WorkspaceView = {
  id: number | null;
  edit: string | null;
  search: string;
  query: ContactQuery;
  selected: ReadonlySet<number>;
  columns: ReadonlySet<string>;
  autoColumns: boolean;
  page: ContactPageResult | null;
  detail: ContactDetailResult | null;
  open: number | null;
  preview: ContactRow | null;
  detailError: string | null;
  busy: boolean;
  slow: boolean;
  contextRights: ContactRights | null;
  error: string | null;
  tenantChanged: boolean;
  sessionEnded: boolean;
  recoveryUrl: string;
  scopeNotice: string | null;
  hiddenCount: number;
  tagColors: ReadonlyMap<string, string | null>;
  tagUsage: ReadonlyMap<string, number>;
  copyBusy: boolean;
  copied: boolean;
  copiedCount: number;
  copyReceipt: number;
  copyError: string | null;
  copyText: string | null;
  copyPageExpired: boolean;
  copyReloadUrl: string | null;
  extractFormat: string;
  extractBusy: boolean;
  extractError: string | null;
  exportedMessage: string | null;
  exportReceipt: number;
  mobileLoading: boolean;
  mobileError: string | null;
  contentWidth: number;
  viewportWidth: number;
  mobileSortOpen: boolean;
  sortDraft: string;
  scopeRevision: string | null;
  returnTags: string | null;
};

type Prefetch = {
  query: string;
  id: number | null;
  context: Promise<void>;
  page: Promise<ContactPageResult> | null;
  detail: Promise<ContactDetailResult> | null;
  colors: Promise<TagCatalogRow[] | null> | null;
  lifetime: AbortController;
};

const isCancel = (error: unknown, signal?: AbortSignal) =>
  (error instanceof DOMException && error.name === "AbortError") || !!signal?.aborted;

/** Espera como Task.Delay con cancelación. */
const delay = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("cancelado", "AbortError"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("cancelado", "AbortError"));
      },
      { once: true },
    );
  });

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export class ContactWorkspaceController {
  readonly api: ContactDirectoryClient;
  readonly store: StoreApi<WorkspaceView>;
  // Lo que se ve (y se copia a `view` en cada cambio).
  private id: number | null = null;
  private edit: string | null = null;
  private search = "";
  private page: ContactPageResult | null = null;
  private detail: ContactDetailResult | null = null;
  private open: number | null = null;
  private preview: ContactRow | null = null;
  private detailError: string | null = null;
  private busy = false;
  private slow = false;
  private error: string | null = null;
  private tenantChanged = false;
  private sessionEnded = false;
  private recoveryPath = "/portal/chat/contactos";
  private scopeNotice: string | null = null;
  private hiddenCount = 0;
  private tagColors = new Map<string, string | null>();
  private tagUsage = new Map<string, number>();
  private copyBusy = false;
  private copied = false;
  private copiedCount = 0;
  private copyReceipt = 0;
  private copyError: string | null = null;
  private copyTextValue: string | null = null;
  private copyPageExpired = false;
  private copyReloadUrl: string | null = null;
  private extractFormat = "CSV";
  private extractBusy = false;
  private extractError: string | null = null;
  private exportedMessage: string | null = null;
  private exportReceipt = 0;
  private mobileRows: ContactRow[] = [];
  private mobileLoading = false;
  private mobileError: string | null = null;
  private contentWidth = 900;
  private viewportWidth = 0;
  private mobileSortOpen = false;
  private sortDraft = "";
  // Lo que no se ve.
  private pending = new AbortController();
  private detailPending = new AbortController();
  private revision = 0;
  private bound = false;
  private needsLoad = false;
  private restoreScrollPending = false;
  private observedScope: string | null = null;
  private checkingContext = false;
  private searchBase: ContactPageResult | null = null;
  private prefetch: Prefetch | null = null;
  private exportRequested = false;
  private disposed = false;
  importWizard: ImportWizardHandle | null = null;
  editor: EditorHandle | null = null;
  /** Elementos de la pantalla que el controlador abre o enfoca (diálogos, hoja, buscador). */
  elements: {
    extractDialog: HTMLDialogElement | null;
    mobileSheet: HTMLDialogElement | null;
    searchInput: HTMLInputElement | null;
    copyDialog: { show(): void } | null;
  } = { extractDialog: null, mobileSheet: null, searchInput: null, copyDialog: null };
  /** Navegación del portal (react-router) para lo que en Blazor es Navigation.NavigateTo. */
  navigate: (path: string, replace?: boolean) => void = (path) => location.assign(path);
  /** A dónde va el foco tras cambiar de hoja («title» o «entry»). */
  sheetFocus: "title" | "entry" | null = null;

  constructor(
    readonly labels: Record<string, string>,
    readonly locale: string,
    readonly tenant: number,
    readonly user: number,
  ) {
    this.api = new ContactDirectoryClient(tenant, user);
    this.store = createStore<WorkspaceView>(() => this.snapshot());
  }

  // ---- Textos ----
  L = (key: string) => this.labels["C1_" + key] ?? `[C1_${key}]`;
  L1 = (key: string) => this.labels["E1_" + key] ?? `[E1_${key}]`;
  L2 = (key: string) => this.labels["C2_" + key] ?? `[C2_${key}]`;
  L3 = (key: string) => this.labels["E3_" + key] ?? `[E3_${key}]`;
  field = (name: string) => this.L("FIELD_" + name.toUpperCase());
  date = (value: string | null | undefined) =>
    contactDate(
      value,
      new Date(),
      this.page?.timezone ?? this.detail?.timezone ?? "UTC",
      this.locale,
      this.L("TODAY"),
      this.L("YESTERDAY"),
    );
  tagColor = (name: string) => {
    const key = name.toLowerCase();
    return this.tagColors.has(key) ? (this.tagColors.get(key) ?? null) : (knownTagColor(key) ?? null);
  };
  /** J4 · C22 1.14.4: el color de la etiqueta todavía no se conoce (sin catálogo ni recuerdo de Etiquetas). */
  tagColorPending = (name: string) =>
    !this.tagColors.has(name.toLowerCase()) && knownTagColor(name) === undefined && this.tagColors.size === 0;
  tagUsageOf = (name: string) => this.tagUsage.get(name.toLowerCase()) ?? 0;
  listUrl = (id: number | null = null, edit: string | null = null) => viewUrl(id, edit);
  get scopeRevision() {
    return this.observedScope;
  }
  /** El orden en palabras del dato: fechas «Más reciente/Más antiguo», Teléfono «Menor a mayor», textos «A–Z». */
  orderLabel = (column: string, descending: boolean) =>
    this.L(
      isDateColumn(column)
        ? descending
          ? "ORDER_RECENT"
          : "ORDER_OLDEST"
        : column === "PHONE"
          ? descending
            ? "ORDER_HIGH"
            : "ORDER_LOW"
          : descending
            ? "ORDER_ZA"
            : "ORDER_AZ",
    );
  sortFieldLabel = (column: string) =>
    this.field(["Contact", ...contactFields].find((name) => sortField(name) === column) ?? "Contact");

  /** Publica lo que se ve (StateHasChanged). */
  changed(): void {
    if (!this.disposed) this.store.setState(this.snapshot(), true);
  }
  private snapshot(): WorkspaceView {
    const state = viewState();
    return {
      id: this.id,
      edit: this.edit,
      search: this.search,
      query: state.query,
      selected: new Set(state.selected),
      columns: new Set(state.columns),
      autoColumns: state.autoColumns,
      page: this.page,
      detail: this.detail,
      open: this.open,
      preview: this.preview,
      detailError: this.detailError,
      busy: this.busy,
      slow: this.slow,
      contextRights: this.page?.access ?? this.api.currentRights ?? null,
      error: this.error,
      tenantChanged: this.tenantChanged,
      sessionEnded: this.sessionEnded,
      recoveryUrl: this.sessionEnded
        ? "/login?ReturnUrl=" + encodeURIComponent(this.recoveryPath) + "&returnOrg=" + this.tenant
        : this.recoveryPath,
      scopeNotice: this.scopeNotice,
      hiddenCount: this.hiddenCount,
      tagColors: new Map(this.tagColors),
      tagUsage: new Map(this.tagUsage),
      copyBusy: this.copyBusy,
      copied: this.copied,
      copiedCount: this.copiedCount,
      copyReceipt: this.copyReceipt,
      copyError: this.copyError,
      copyText: this.copyTextValue,
      copyPageExpired: this.copyPageExpired,
      copyReloadUrl: this.copyReloadUrl,
      extractFormat: this.extractFormat,
      extractBusy: this.extractBusy,
      extractError: this.extractError,
      exportedMessage: this.exportedMessage,
      exportReceipt: this.exportReceipt,
      mobileLoading: this.mobileLoading,
      mobileError: this.mobileError,
      contentWidth: this.contentWidth,
      viewportWidth: this.viewportWidth,
      mobileSortOpen: this.mobileSortOpen,
      sortDraft: this.sortDraft,
      scopeRevision: this.observedScope,
      returnTags: state.returnToTags,
    };
  }

  // ---- Derivados ----
  private get current() {
    return this.edit !== null ? this.id : this.open;
  }
  get isMobile() {
    return this.contentWidth < 768;
  }
  get showDirectory() {
    return this.viewportWidth <= 0 || this.viewportWidth < 768;
  }
  private get mobileHasMore() {
    return (
      this.page !== null &&
      this.isMobile &&
      this.open === null &&
      this.edit === null &&
      this.mobileRows.length < this.page.total
    );
  }
  get hasFilters() {
    const query = viewState().query;
    return (
      !!query.search?.trim() ||
      (query.tags?.length ?? 0) > 0 ||
      query.inboxId !== null ||
      query.agentUserId !== null ||
      query.period !== "ANY"
    );
  }
  // J4 · C22 1.14.4: el esqueleto usa las columnas de la vista que se va a mostrar; sin página, la cantidad de bandejas
  // de la última lista de esta persona en este navegador (decide si va «Bandeja»).
  /** J4 · la cantidad de bandejas ya se conoce (página cargada o recordada en este navegador). */
  viewKnown = () => {
    if (this.page) return true;
    try {
      return localStorage.getItem(`bheli.contacts.inboxes.${this.tenant}.${this.user}`) !== null;
    } catch {
      return false;
    }
  };
  baseFields = (page: ContactPageResult | null) => {
    const key = `bheli.contacts.inboxes.${this.tenant}.${this.user}`;
    if (page) {
      try {
        if (localStorage.getItem(key) !== String(page.inboxes.length)) localStorage.setItem(key, String(page.inboxes.length));
      } catch {
        // Sin almacenamiento local, el esqueleto toma 0 bandejas.
      }
      return automaticFields(page.inboxes.length);
    }
    let known = 0;
    try {
      known = Number(localStorage.getItem(key) ?? 0) || 0;
    } catch {
      known = 0;
    }
    return automaticFields(known);
  };
  visibleFields = (view: WorkspaceView) =>
    view.autoColumns ? this.baseFields(view.page) : contactFields.filter((name) => view.columns.has(name));

  // ---- Mensajes de error (Failure) ----
  failure = (exception: unknown): string => {
    const code = exception instanceof ContactRequestFailure ? exception.code : null;
    if (code && ["TAG_ARCHIVED", "TAG_NOT_FOUND", "TAG_DUPLICATE_ARCHIVED", "TAG_DUPLICATE"].includes(code))
      return this.L1(code);
    if (code === "SESSION_INVALID") {
      this.invalidate("SESSION_ENDED");
      return this.L("SESSION_ENDED");
    }
    if (code === "TENANT_CHANGED") this.invalidate();
    if (code === "ACCESS_REVOKED") {
      if (this.api.currentRights?.canRead !== true) this.invalidate();
      else this.reconcileImportAuthority();
    }
    if (code === "CONTACT_SCOPE_CHANGED") return this.L2("SCOPE_RETRY");
    if (code === "IMPORT_SEPARATOR") return this.L2("IMPORT_SEPARATOR");
    const map: Record<string, string> = {
      CRM_FORBIDDEN: "DENIED",
      SESSION_INVALID: "DENIED",
      ACCESS_REVOKED: "DENIED",
      CRM_NOT_FOUND: "MISSING",
      STALE_VERSION: "STALE",
      CRM_CONFLICT: "KEY_CONFLICT",
      VERIFIED_FIELD_LOCKED: "VERIFIED",
      TENANT_CHANGED: "TENANT_CHANGED",
      IMPORT_MAPPING: "MAPPING_ERROR",
      IMPORT_FORMAT: "IMPORT_FORMAT",
      IMPORT_LIMIT: "IMPORT_LIMIT",
      IMPORT_EXPIRED: "IMPORT_EXPIRED",
      CONTACT_PAGE_EXPIRED: "PAGE_EXPIRED",
      EXPORT_LIMIT: "EXPORT_LIMIT",
      INVALID_REQUEST: "INVALID",
    };
    return this.L(code ? (map[code] ?? "UNAVAILABLE") : "UNAVAILABLE");
  };

  /** Retira los datos de una sesión que ya no autoriza esta página, con motivo y regreso explícitos. */
  private invalidate(reason = "TENANT_CHANGED") {
    this.recoveryPath = viewUrl(this.current, this.edit);
    this.pending.abort();
    this.revision++;
    clearViewState();
    this.page = null;
    this.detail = null;
    this.searchBase = null;
    this.tagColors.clear();
    this.tagUsage.clear();
    this.copyTextValue = null;
    this.copied = false;
    this.tenantChanged = true;
    this.sessionEnded = reason === "SESSION_ENDED";
    this.error = this.L(reason);
    this.busy = false;
    this.importWizard?.invalidate();
  }

  // ---- Ruta (OnParametersSetAsync) ----
  setRoute(id: number | null, edit: string | null, search: string): void {
    const returnUrl = tagReturnUrl(search);
    const state = viewState();
    if (state.owner.length > 0 && state.owner !== `${this.tenant}:${this.user}`) {
      this.renew();
      this.revision++;
      this.importWizard?.invalidate();
      this.page = null;
      this.detail = null;
      this.searchBase = null;
      this.tagColors.clear();
      this.tagUsage.clear();
      this.copyTextValue = null;
      this.copied = false;
      this.observedScope = null;
    }
    setViewOwner(this.tenant, this.user);
    if (this.viewportWidth <= 0) this.viewportWidth = innerWidth;
    this.id = id;
    this.edit = edit;
    this.search = search;
    state.query = parseContactQuery(search);
    state.returnToTags = returnUrl;
    // Entrar con la dirección de un contacto abre su ficha; navegar a la lista la cierra.
    if (this.open !== id) {
      this.open = id;
      this.preview = null;
      this.detailError = null;
    }
    if (!this.bound && this.prefetch === null) this.prefetch = this.startPrefetch();
    if (this.bound) void this.load();
    else {
      this.needsLoad = true;
      this.busy = true;
    }
    this.changed();
  }

  private renew() {
    this.pending.abort();
    this.pending = new AbortController();
  }

  /** Lo primero en el navegador: la lista (y la ficha y las etiquetas) salen junto con el contexto (C9-05). */
  private startPrefetch(): Prefetch {
    const lifetime = new AbortController();
    const signal = lifetime.signal;
    const query = viewState().query;
    const id = this.current;
    const context = this.api.ensureContext(signal);
    context.catch(() => undefined);
    const list = id === null || this.edit === null;
    const detail =
      id !== null
        ? (async () => {
            await context;
            return this.api.get(id, signal);
          })()
        : null;
    detail?.catch(() => undefined);
    const page = list ? this.api.listWithContext(context, query, signal) : null;
    page?.catch(() => undefined);
    const colors = list ? this.readTagCatalog() : null;
    return { query: queryKey(query), id, context, page, detail, colors, lifetime };
  }

  private takePrefetch(): Prefetch | null {
    const ahead = this.prefetch;
    this.prefetch = null;
    if (!ahead) return null;
    if (ahead.query === queryKey(viewState().query) && ahead.id === this.current) return ahead;
    ahead.lifetime.abort();
    return null;
  }

  /** Catálogo completo de etiquetas (colores y uso); null si perdió permiso (el directorio sigue disponible). */
  private async readTagCatalog(): Promise<TagCatalogRow[] | null> {
    const catalog = new TagCatalogClient(this.tenant, this.user);
    try {
      return await readAllTags((number) =>
        catalog.list({
          search: null,
          state: "ALL",
          from: null,
          to: null,
          sort: "CREATED",
          page: number,
          pageSize: 100,
          selectedIds: null,
          selectedNames: null,
        }),
      );
    } catch (error) {
      if (error instanceof ContactRequestFailure) return null;
      throw error;
    }
  }

  // ---- Enlace con el navegador (OnAfterRenderAsync del primer render) ----
  async bind(): Promise<void> {
    if (this.bound) return;
    this.bound = true;
    const state = viewState();
    const saved = readStored(state.owner);
    if (saved) {
      try {
        if (saved.ownerChanged) {
          state.query = parseEmpty();
          state.selected.clear();
          state.importBatchId = null;
        }
        await (this.prefetch?.context ?? this.api.ensureContext(this.pending.signal));
        const authority = mask(this.api.currentRights!);
        if (saved.snapshot) {
          const query = state.query;
          const strip = (value: ContactQuery) => queryKey({ ...value, page: 1, pageSize: 25, sort: "NAME" });
          const sameFilter = strip(query) === strip(creationDated(saved.snapshot.query));
          restore(saved.snapshot);
          state.query = query;
          if (state.pendingScopeNotice) this.scopeNotice = this.L2("SCOPE_UPDATED");
          if (!sameFilter) {
            state.selected.clear();
            state.scroll = 0;
          }
          if (saved.snapshot.sessionStamp !== this.api.currentSessionStamp) {
            state.selected.clear();
            state.importBatchId = null;
            state.importPreparation = null;
            state.scroll = 0;
          }
          this.reconcileImportAuthority();
          this.observedScope = saved.snapshot.scopeRevision;
          this.restoreScrollPending = this.edit === null;
          if (state.importPreparation === null && state.importBatchId && this.canPrepareImport(this.api.currentRights))
            await this.importWizard?.restore(state.importBatchId);
        }
        state.authorityMask = authority;
        state.sessionStamp = this.api.currentSessionStamp;
      } catch (error) {
        if (error instanceof ContactRequestFailure) this.error = this.failure(error);
        else if (!isCancel(error)) this.error = this.L("UNAVAILABLE");
      }
    }
    if (state.importPreparation && !this.tenantChanged) {
      try {
        await this.api.ensureContext(this.pending.signal);
        this.reconcileImportAuthority();
        await this.importWizard?.restorePreparation();
        state.sessionStamp = this.api.currentSessionStamp;
      } catch (error) {
        if (error instanceof ContactRequestFailure) this.error = this.failure(error);
        else if (!isCancel(error)) this.error = this.L("UNAVAILABLE");
      }
    }
    if (this.needsLoad && !this.tenantChanged) {
      this.needsLoad = false;
      await this.load();
    }
    this.changed();
    if (
      !this.exportRequested &&
      state.returnToTags !== null &&
      this.search.replace(/^\?/, "").split("&").includes("extract=1") &&
      this.page?.access.canExport === true
    ) {
      this.exportRequested = true;
      this.exportOpen();
    }
  }

  /** Lo que el portal actual hace después de cada render: el desplazamiento guardado y el foco de la hoja. */
  afterRender(sortTitle: HTMLElement | null, sortEntry: HTMLElement | null): void {
    if (this.restoreScrollPending && !this.busy) {
      this.restoreScrollPending = false;
      dom.restoreScroll(viewState().scroll);
    }
    if (this.sheetFocus) {
      const target = this.sheetFocus;
      this.sheetFocus = null;
      (target === "title" ? sortTitle : sortEntry)?.focus();
    }
  }

  /** Los elementos y piezas de la pantalla que el controlador abre o consulta (después de cada dibujo). */
  attach(parts: {
    extractDialog: HTMLDialogElement | null;
    mobileSheet: HTMLDialogElement | null;
    searchInput: HTMLInputElement | null;
    copyDialog: { show(): void } | null;
    importWizard: ImportWizardHandle | null;
    editor: EditorHandle | null;
  }): void {
    const { importWizard, editor, ...elements } = parts;
    this.elements = elements;
    this.importWizard = importWizard;
    this.editor = editor;
  }

  /** La dirección cambió (o es la primera): con qué navegar y qué mostrar. */
  route(
    navigate: (path: string, replace?: boolean) => void,
    id: number | null,
    edit: string | null,
    search: string,
  ): void {
    this.navigate = navigate;
    // Atrás puede llegar dos veces (el evento del navegador y el enrutador): la misma dirección en el mismo momento se lee una vez.
    const key = `${id}|${edit}|${search}`;
    if (key === this.lastRoute.key && performance.now() - this.lastRoute.at < 100) return;
    this.lastRoute = { key, at: performance.now() };
    this.setRoute(id, edit, search);
  }
  private lastRoute = { key: "", at: -Infinity };

  /** Vuelve a montar el mismo espacio (React en desarrollo monta dos veces): sigue publicando cambios. */
  revive(): void {
    if (!this.disposed) return;
    this.disposed = false;
    this.renew();
    this.detailPending = new AbortController();
  }

  dispose(): void {
    this.disposed = true;
    this.pending.abort();
    this.detailPending.abort();
    this.prefetch?.lifetime.abort();
  }

  // ---- Permisos de la importación ----
  private canPrepareImport = (value: ContactRights | null) =>
    !!value && value.canRead && value.canImport && value.canWrite && value.globalScope;
  private accessTaskChanged() {
    this.scopeNotice = this.L2("SCOPE_UPDATED");
    viewState().pendingScopeNotice = true;
  }
  /** Conserva archivo y lote mientras su autoridad propia siga vigente, aunque cambie Messaging. */
  private reconcileImportAuthority() {
    const state = viewState();
    if (!this.canPrepareImport(this.api.currentRights)) {
      if (state.importBatchId || state.importPreparation || this.importWizard?.hasPreparation())
        this.accessTaskChanged();
      state.importBatchId = null;
      state.importPreparation = null;
      this.importWizard?.invalidate(this.L("DENIED"));
    }
  }

  /** Retira el estado que depende del acceso; conserva filtros válidos y trabajo autorizado. */
  private async scopeChanged() {
    this.copyTextValue = null;
    this.copied = false;
    this.searchBase = null;
    this.tagColors.clear();
    this.tagUsage.clear();
    this.reconcileImportAuthority();
    await revalidateContactScope(this.api.currentRights?.canOpenChat === true);
  }

  /** Revalida la selección entre páginas en bloques, con la consulta propietaria. */
  private async reconcileSelection(signal: AbortSignal) {
    const state = viewState();
    if (state.selected.size === 0 || this.api.currentRights?.globalScope === true) return;
    const selected = [...state.selected];
    const valid = new Set<number>();
    for (let start = 0; start < selected.length; start += 1000) {
      const block = selected.slice(start, start + 1000);
      for (let number = 1; number <= 10; number++) {
        const result = await this.api.list(
          { ...parseEmpty(), page: number, pageSize: 100, selectedIds: block },
          signal,
        );
        for (const row of result.items) valid.add(row.contact.id);
        if (number * 100 >= result.total) break;
      }
    }
    if (signal.aborted) throw new DOMException("cancelado", "AbortError");
    let removed = 0;
    for (const id of selected)
      if (!valid.has(id)) {
        state.selected.delete(id);
        removed++;
      }
    if (removed > 0) this.accessTaskChanged();
  }

  private static remainingFilters(query: ContactQuery, result: ContactPageResult): ContactQuery {
    return {
      ...query,
      inboxId:
        query.inboxId !== null && !result.inboxes.some((inbox) => inbox.id === query.inboxId) ? null : query.inboxId,
      agentUserId:
        query.agentUserId !== null &&
        !result.agents.some((agent) => agent.id === query.agentUserId && agent.kind === query.agentKind)
          ? null
          : query.agentUserId,
      agentKind: query.agentKind === "NONE" && !result.hasUnassigned ? "HUMAN" : query.agentKind,
    };
  }

  private rememberTags(rows: TagCatalogRow[] | null) {
    // KF2 · C15: el color guardado de cada etiqueta; sin color guardado, neutra (nunca se calcula).
    for (const row of rows ?? []) {
      this.tagColors.set(row.tag.name.toLowerCase(), row.tag.color);
      this.tagUsage.set(row.tag.name.toLowerCase(), row.contacts ?? 0);
    }
  }

  // ---- Carga (LoadAsync) ----
  async load(): Promise<void> {
    this.renew();
    const signal = this.pending.signal;
    const current = ++this.revision;
    const state = viewState();
    const previousPage = this.page;
    if (previousPage !== null && this.edit === null) {
      const captured = dom.readScroll();
      if (current !== this.revision || signal.aborted) return;
      state.scroll = captured;
      this.restoreScrollPending = true;
    }
    this.busy = true;
    this.slow = false;
    this.error = null;
    // I13/C22 · la barra fina aparece si la carga pasa de 1 s.
    const unslow = window.setTimeout(() => {
      if (current === this.revision && this.busy) {
        this.slow = true;
        this.changed();
      }
    }, 1000);
    // I13/C22 · los permisos se publican al confirmarse el contexto, sin esperar la lista.
    void this.api.ensureRecentContext(signal).then(
      () => {
        if (current === this.revision && !signal.aborted) this.changed();
      },
      () => undefined,
    );
    // Otra entidad nunca reutiliza la ficha anterior.
    if (this.detail?.contact.id !== this.current) this.detail = null;
    this.changed();
    try {
      for (let attempt = 0; attempt < 3; attempt++) {
        const requestedScope = this.api.currentScope;
        try {
          const ahead = attempt === 0 ? this.takePrefetch() : null;
          const colorsAhead =
            ahead?.colors ??
            (attempt === 0 &&
            this.tagColors.size === 0 &&
            (this.id === null || this.edit === null) &&
            this.api.currentRights?.canReadTags !== false
              ? this.readTagCatalog()
              : null);
          colorsAhead?.catch(() => undefined);
          const shown = this.current;
          const detailTask = shown !== null ? (ahead?.detail ?? this.api.get(shown, signal)) : null;
          const pageTask =
            this.id === null || this.edit === null
              ? (ahead?.page ?? this.api.list(batchSized(state.query, this.isMobile), signal))
              : null;
          detailTask?.catch(() => undefined);
          pageTask?.catch(() => undefined);
          const loadedDetail = detailTask ? await detailTask : null;
          const loadedPage = pageTask ? await pageTask : null;
          if (current !== this.revision || signal.aborted) return;
          if (state.sessionStamp !== null && state.sessionStamp !== this.api.currentSessionStamp) {
            this.invalidate("SESSION_ENDED");
            return;
          }
          const nextScope = loadedPage?.scopeRevision ?? loadedDetail?.scopeRevision ?? this.api.currentScope;
          const scopeChanged = this.observedScope !== null && this.observedScope !== nextScope;
          if (scopeChanged) await this.scopeChanged();
          this.reconcileImportAuthority();
          if (scopeChanged || state.selected.size > 0) await this.reconcileSelection(signal);
          if (nextScope !== this.api.currentScope) throw new ContactRequestFailure(409, "CONTACT_SCOPE_CHANGED");
          if (current !== this.revision || signal.aborted) return;
          this.observedScope = nextScope;
          state.scopeRevision = nextScope;
          if (loadedPage) {
            const cleaned = ContactWorkspaceController.remainingFilters(state.query, loadedPage);
            if (!sameQuery(cleaned, state.query)) {
              if (scopeChanged) this.accessTaskChanged();
              state.query = { ...cleaned, page: 1 };
              this.persist();
              this.navigate(viewUrl(), true);
              return;
            }
          }
          if (
            scopeChanged &&
            previousPage &&
            loadedPage &&
            previousPage.items.some(
              (old) =>
                old.conversation?.sessionId !== null &&
                old.conversation?.sessionId !== undefined &&
                loadedPage.items.some(
                  (row) =>
                    row.contact.id === old.contact.id &&
                    (row.conversation?.sessionId === null || row.conversation?.sessionId === undefined),
                ),
            )
          )
            this.accessTaskChanged();
          if (loadedPage && state.query.search && this.searchBase === null)
            this.searchBase = await this.api.list(
              batchSized({ ...state.query, search: null, page: 1 }, this.isMobile),
              signal,
            );
          if (current !== this.revision || signal.aborted) return;
          this.detail = loadedDetail;
          this.page = loadedPage;
          // K4 · C17: en el celular la lista acumula de 25 en 25; al volver del detalle se reconstruye 1..N.
          if (this.page && this.isMobile && this.edit === null) {
            const page = this.page;
            if (state.query.page <= 1) this.mobileRows = [...page.items];
            else if (this.mobileRows.length === 0) {
              const rows: ContactRow[] = [];
              for (let number = 1; number < state.query.page; number++) {
                const previous = await this.api.list(
                  batchSized({ ...state.query, page: number }, this.isMobile),
                  signal,
                );
                if (current !== this.revision || signal.aborted) return;
                rows.push(...previous.items);
                if (rows.length >= page.total) break;
              }
              rows.push(...page.items.filter((row) => !rows.some((item) => item.contact.id === row.contact.id)));
              this.mobileRows = rows;
            } else if (this.mobileRows.length < page.total && state.query.page > 1)
              this.mobileRows = [
                ...this.mobileRows,
                ...page.items.filter((row) => !this.mobileRows.some((item) => item.contact.id === row.contact.id)),
              ];
            if (this.mobileRows.length > 0) this.page = { ...page, items: [...this.mobileRows] };
            this.mobileError = null;
          } else if (!this.isMobile) {
            this.mobileRows = [];
            this.mobileError = null;
          }
          if (this.page && !state.query.search) this.searchBase = this.page;
          this.hiddenCount = 0;
          if (
            this.page?.total === 0 &&
            state.query.search &&
            ((state.query.tags?.length ?? 0) > 0 ||
              state.query.inboxId !== null ||
              state.query.agentUserId !== null ||
              state.query.period !== "ANY")
          )
            this.hiddenCount = (
              await this.api.list({ ...parseEmpty(), search: state.query.search, pageSize: 1 }, signal)
            ).total;
          if ((this.page?.access ?? this.detail?.access)?.canReadTags === true && this.tagColors.size === 0) {
            const colors = await (colorsAhead && !scopeChanged ? colorsAhead : this.readTagCatalog());
            this.rememberTags(colors);
          }
          if (this.page) state.query = { ...state.query, page: this.page.page };
          state.authorityMask = mask(this.api.currentRights!);
          state.sessionStamp = this.api.currentSessionStamp;
          state.pendingScopeNotice = false;
          this.persist();
          return;
        } catch (error) {
          if (error instanceof ContactRequestFailure && error.code === "CONTACT_SCOPE_CHANGED" && attempt < 2) {
            if (current !== this.revision || signal.aborted) return;
            await this.scopeChanged();
            continue;
          }
          if (
            error instanceof ContactRequestFailure &&
            error.code === "CRM_FORBIDDEN" &&
            this.id === null &&
            (state.query.inboxId !== null || state.query.agentUserId !== null) &&
            attempt < 2
          ) {
            // Un filtro de bandeja retirado puede ser 403 aunque Contactos siga autorizado.
            await this.api.ensureContext(signal);
            if (current !== this.revision || signal.aborted) return;
            if (
              requestedScope === this.api.currentScope &&
              (this.observedScope === null || this.observedScope === this.api.currentScope) &&
              !state.pendingScopeNotice
            )
              throw error;
            await this.scopeChanged();
            if (current !== this.revision || signal.aborted) return;
            const remaining = await this.api.list(
              { ...state.query, inboxId: null, agentUserId: null, page: 1 },
              signal,
            );
            if (current !== this.revision || signal.aborted) return;
            const cleaned = ContactWorkspaceController.remainingFilters(state.query, remaining);
            if (!sameQuery(cleaned, state.query)) {
              this.accessTaskChanged();
              state.query = { ...cleaned, page: 1 };
              state.scopeRevision = remaining.scopeRevision;
              this.persist();
              if (current !== this.revision || signal.aborted) return;
              this.navigate(viewUrl(), true);
              return;
            }
            continue;
          }
          throw error;
        }
      }
    } catch (error) {
      if (!isCancel(error, signal) && current === this.revision) this.error = this.failure(error);
    } finally {
      clearTimeout(unslow);
      if (current === this.revision) {
        this.busy = false;
        this.slow = false;
      }
      this.changed();
    }
  }

  /** Después de cambiar etiquetas en la ficha: comprueba la sesión y relee la lista y la ficha en silencio (C9-03). */
  checkContext = () => this.verifyContext(true);
  /** C10-01: al volver a la ventana solo se comprueban la sesión y los permisos. */
  windowReturned = () => this.verifyContext(false);

  private async verifyContext(refresh: boolean) {
    if (this.checkingContext || this.tenantChanged) return;
    this.checkingContext = true;
    const state = viewState();
    try {
      await this.api.ensureContext(this.pending.signal);
      if (state.sessionStamp !== null && state.sessionStamp !== this.api.currentSessionStamp) {
        this.invalidate("SESSION_ENDED");
        this.changed();
        return;
      }
      const authority = mask(this.api.currentRights!);
      const changed =
        (this.observedScope !== null && this.observedScope !== this.api.currentScope) ||
        (state.authorityMask !== null && state.authorityMask !== authority);
      if (changed) {
        this.renew();
        this.revision++;
        this.busy = false;
        await this.scopeChanged();
      }
      state.authorityMask = authority;
      state.sessionStamp = this.api.currentSessionStamp;
      this.reconcileImportAuthority();
      const withdrawn =
        this.api.currentRights?.canExport !== true &&
        (state.selected.size > 0 || this.copyTextValue !== null || this.copied);
      if (this.api.currentRights?.canExport !== true) {
        state.selected.clear();
        this.copyTextValue = null;
        this.copied = false;
      }
      if (this.edit !== null && changed) {
        const current = this.revision,
          signal = this.pending.signal,
          id = this.id;
        const loadedDetail = id !== null ? await this.api.get(id, signal) : null;
        const loadedPage = id !== null ? null : await this.api.list(batchSized(state.query, this.isMobile), signal);
        if (current !== this.revision || signal.aborted || this.id !== id) return;
        this.detail = loadedDetail;
        this.page = loadedPage;
        this.observedScope = loadedDetail?.scopeRevision ?? loadedPage?.scopeRevision ?? this.api.currentScope;
        state.scopeRevision = this.observedScope;
        this.persist();
      } else if (!this.busy && this.edit === null && (changed || refresh))
        await (changed ? this.load() : this.refreshQuietly());
      else if (!changed && !withdrawn) return;
      this.changed();
    } catch (error) {
      if (error instanceof ContactRequestFailure) {
        if (error.code === "CRM_NOT_FOUND") {
          this.detail = null;
          this.page = null;
        }
        this.error = this.failure(error);
        this.changed();
      }
    } finally {
      this.checkingContext = false;
    }
  }

  /** La lista y la ficha se releen sin barra, sin esqueleto y sin mover el desplazamiento; si falla, queda lo que se veía. */
  private async refreshQuietly() {
    if (this.page === null && this.detail === null) return;
    const current = this.revision,
      signal = this.pending.signal,
      id = this.open;
    const state = viewState();
    try {
      const listTask = this.page === null ? null : this.api.list(batchSized(state.query, this.isMobile), signal);
      const detailTask = id !== null && this.detail !== null ? this.api.get(id, signal) : null;
      listTask?.catch(() => undefined);
      detailTask?.catch(() => undefined);
      const loadedPage = listTask ? await listTask : null;
      const loadedDetail = detailTask ? await detailTask : null;
      if (current !== this.revision || signal.aborted || this.busy) return;
      const nextScope = loadedPage?.scopeRevision ?? loadedDetail?.scopeRevision ?? null;
      if (this.observedScope !== null && nextScope !== null && nextScope !== this.observedScope) {
        await this.scopeChanged();
        await this.load();
        return;
      }
      if (loadedPage && this.page) {
        const cleaned = ContactWorkspaceController.remainingFilters(state.query, loadedPage);
        if (!sameQuery(cleaned, state.query)) {
          state.query = { ...cleaned, page: 1 };
          this.persist();
          this.navigate(viewUrl(), true);
          return;
        }
        const merged = this.mergeRows(loadedPage);
        if (!sameJson(merged, this.page)) {
          this.page = merged;
          if (!state.query.search) this.searchBase = this.page;
        }
      }
      if (loadedDetail && this.detail && this.open === id) {
        const next =
          loadedDetail.conversations.length < this.detail.conversations.length
            ? { ...this.detail, contact: loadedDetail.contact, access: loadedDetail.access }
            : loadedDetail;
        if (!sameJson(next, this.detail)) this.detail = next;
      }
    } catch (error) {
      if (isCancel(error, signal)) return;
      if (error instanceof ContactRequestFailure && error.code === "CONTACT_SCOPE_CHANGED") {
        if (current === this.revision) {
          await this.scopeChanged();
          await this.load();
        }
      } else if (
        error instanceof ContactRequestFailure &&
        ["SESSION_INVALID", "TENANT_CHANGED", "ACCESS_REVOKED"].includes(error.code)
      ) {
        if (current === this.revision) this.error = this.failure(error);
      }
      /* Lo demás: sigue lo que se veía. */
    }
  }

  private mergeRows(fresh: ContactPageResult): ContactPageResult {
    if (!this.isMobile || this.mobileRows.length === 0) return fresh;
    const rows = new Map(fresh.items.map((row) => [row.contact.id, row]));
    this.mobileRows = this.mobileRows.map((row) => rows.get(row.contact.id) ?? row);
    return { ...fresh, items: [...this.mobileRows], page: viewState().query.page };
  }

  // ---- Ficha (C9-02) ----
  /** En el celular con selección, tocar una fila la marca; si no, abre la ficha. */
  async openContact(id: number): Promise<void> {
    if (this.contentWidth < 768 && viewState().selected.size > 0) {
      this.select(id, !viewState().selected.has(id));
      return;
    }
    await this.showContact(id);
  }
  /** El nombre es un enlace: con Ctrl o el botón del medio abre otra pestaña; el toque normal abre la ficha aquí. */
  openFromLink(event: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; altKey: boolean }, id: number) {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    void this.openContact(id);
  }
  private async showContact(id: number) {
    if (this.open === id && (this.detail !== null || this.preview !== null)) return;
    this.open = id;
    this.preview = this.page?.items.find((row) => row.contact.id === id) ?? null;
    this.detailError = null;
    if (this.detail?.contact.id !== id) this.detail = null;
    this.changed();
    dom.replacePath(viewUrl(id));
    await this.loadDetail(id);
  }
  private async loadDetail(id: number) {
    this.detailPending.abort();
    this.detailPending = new AbortController();
    const signal = this.detailPending.signal;
    try {
      const value = await this.api.get(id, signal);
      if (signal.aborted || this.open !== id) return;
      if (this.observedScope !== null && value.scopeRevision !== this.observedScope) {
        await this.scopeChanged();
        await this.load();
        return;
      }
      this.detail = value;
    } catch (error) {
      if (isCancel(error, signal)) return;
      if (error instanceof ContactRequestFailure && error.code === "CONTACT_SCOPE_CHANGED") {
        if (this.open === id) {
          await this.scopeChanged();
          await this.load();
        }
        return;
      }
      if (this.open !== id) return;
      // Sesión o empresa cambiadas invalidan la página; lo demás queda dentro de la ficha y la lista sigue a la vista.
      const message = this.failure(error);
      if (this.tenantChanged) this.error = message;
      else this.detailError = message;
    }
    this.changed();
  }
  /** × o Esc: cierra sin recargar y el foco vuelve a la fila que estaba abierta. */
  async closeContact(): Promise<void> {
    const closed = this.open;
    this.detailPending.abort();
    this.open = null;
    this.preview = null;
    this.detail = null;
    this.detailError = null;
    this.changed();
    dom.replacePath(viewUrl());
    if (closed !== null) requestAnimationFrame(() => dom.focusRow(closed));
  }
  /** Escape fuera de diálogos: cierra la ficha o, sin ficha, quita la selección. */
  dismiss = () => {
    if (this.open !== null && this.edit === null) void this.closeContact();
    else {
      this.clearSelection();
      this.changed();
    }
  };
  longSelect = (id: number) => {
    this.select(id, !viewState().selected.has(id));
    this.changed();
  };
  async moreSessions(): Promise<void> {
    const id = this.current;
    if (this.detail?.nextBefore === null || this.detail?.nextBefore === undefined || id === null) return;
    const captured = this.detail,
      current = this.revision,
      signal = this.pending.signal;
    try {
      const value = await this.api.get(id, signal, captured.nextBefore);
      if (signal.aborted || this.revision !== current || this.current !== id || this.detail !== captured) return;
      if (value.scopeRevision !== captured.scopeRevision) {
        await this.scopeChanged();
        await this.load();
        return;
      }
      const seen = new Set<number | null>();
      this.detail = {
        ...value,
        conversations: [...captured.conversations, ...value.conversations].filter((item) => {
          if (seen.has(item.sessionId)) return false;
          seen.add(item.sessionId);
          return true;
        }),
      };
    } catch (error) {
      if (error instanceof ContactRequestFailure && error.code === "CONTACT_SCOPE_CHANGED") {
        await this.scopeChanged();
        await this.load();
      } else if (!isCancel(error, signal)) this.error = this.failure(error);
    }
    this.changed();
  }
  async openSession(thread: ContactSession): Promise<void> {
    try {
      await this.api.ensureContext(this.pending.signal);
      if (this.api.currentRights?.canOpenChat !== true) return;
      if (this.observedScope !== this.api.currentScope) {
        await this.scopeChanged();
        await this.load();
        return;
      }
      const opened =
        thread.conversationId !== null
          ? await openContactConversation(thread.conversationId)
          : thread.sessionId !== null && (await openContactSession(thread.sessionId));
      if (!opened) this.error = this.L("UNAVAILABLE");
    } catch (error) {
      if (error instanceof ContactRequestFailure) this.error = this.failure(error);
    }
    this.changed();
  }

  // ---- Buscar, filtrar, ordenar y paginar ----
  /** Filtra lo ya autorizado al instante y completa desde dos letras; limpiar no consulta al servidor. */
  async searchFor(term: string): Promise<void> {
    this.renew();
    const signal = this.pending.signal;
    this.revision++;
    const state = viewState();
    state.query = { ...state.query, search: term, page: 1 };
    state.selected.clear();
    this.busy = false;
    if (this.searchBase) {
      const rows = this.searchBase.items.filter((row) => matches(row, term));
      this.page = { ...this.searchBase, items: rows, total: term ? rows.length : this.searchBase.total, page: 1 };
    }
    this.persist();
    dom.replacePath(viewUrl());
    this.changed();
    if (term.trim().length < 2) return;
    try {
      // 100 ms desde la última tecla (el portal actual espera 250): el mismo criterio que la búsqueda de Etiquetas.
      await delay(100, signal);
      await this.load();
    } catch (error) {
      if (!isCancel(error, signal)) throw error;
    }
  }
  async clearSearch(): Promise<void> {
    await this.searchFor("");
    this.elements.searchInput?.focus();
  }
  async emptyRecovery(): Promise<void> {
    const state = viewState();
    if (this.hiddenCount > 0 || !state.query.search) await this.filter(clearedQuery(state.query));
    else await this.clearSearch();
  }
  async filter(value: ContactQuery): Promise<void> {
    const state = viewState();
    this.searchBase = null;
    state.query = { ...value, page: 1 };
    state.selected.clear();
    state.scroll = 0;
    this.mobileRows = [];
    this.mobileError = null;
    this.persist();
    await this.showList();
  }
  async order(sort: string): Promise<void> {
    const state = viewState();
    state.query = { ...state.query, sort: sort || "NAME", page: 1 };
    this.mobileRows = [];
    this.mobileError = null;
    this.persist();
    await this.showList();
  }
  sortColumn = (field: string) => this.order(toggleSort(viewState().query.sort, field));
  async pageTo(value: number): Promise<void> {
    viewState().query = { ...viewState().query, page: value };
    this.persist();
    await this.showList();
  }
  async size(value: number): Promise<void> {
    viewState().query = { ...viewState().query, page: 1, pageSize: value };
    this.persist();
    await this.showList();
  }
  /** La lista con la consulta nueva sin pedir el documento: una entrada en el historial y la lista arriba. */
  private async showList() {
    const url = viewUrl();
    // Como al navegar a una dirección sin contacto: la ficha abierta se cierra.
    if (this.open !== null && this.edit === null) {
      this.detailPending.abort();
      this.open = null;
      this.preview = null;
      this.detailError = null;
    }
    dom.pushPath(url);
    viewState().query = parseContactQuery(url.slice(url.indexOf("?")));
    this.search = url.slice(url.indexOf("?"));
    await this.load();
  }

  /** K4 · C17: el siguiente lote de 25 en el celular; si falla, Reintentar repite solo ese lote. */
  loadMore = async () => {
    if (this.mobileLoading || !this.mobileHasMore || this.page === null) return;
    const state = viewState();
    if (this.mobileRows.length === 0 && this.page.items.length > 0 && state.query.page <= 1)
      this.mobileRows = [...this.page.items];
    this.mobileLoading = true;
    this.mobileError = null;
    this.changed();
    try {
      const next = Math.floor(this.mobileRows.length / mobileBatchSize) + 1;
      const result = await this.api.list(batchSized({ ...state.query, page: next }, true), this.pending.signal);
      this.mobileRows = [
        ...this.mobileRows,
        ...result.items.filter((row) => !this.mobileRows.some((item) => item.contact.id === row.contact.id)),
      ];
      this.page = { ...result, items: [...this.mobileRows], page: next };
      state.query = { ...state.query, page: next };
      this.persist();
      dom.replacePath(viewUrl());
    } catch (error) {
      if (!isCancel(error)) this.mobileError = this.failure(error);
    } finally {
      this.mobileLoading = false;
      this.changed();
    }
  };
  retryLoadMore = async () => {
    this.mobileError = null;
    await this.loadMore();
  };

  /** El primer LoadAsync corre con 900 (escritorio); al medir el celular, parte de la página ya cargada. */
  contentWidthChanged = (width: number, viewport = 0) => {
    const shown = `${this.isMobile}:${this.showDirectory}`;
    this.contentWidth = width;
    if (viewport > 0) this.viewportWidth = viewport;
    let changed = shown !== `${this.isMobile}:${this.showDirectory}`;
    if (
      this.isMobile &&
      this.mobileRows.length === 0 &&
      this.page !== null &&
      this.page.items.length > 0 &&
      this.edit === null &&
      viewState().query.page <= 1
    ) {
      this.mobileRows = [...this.page.items];
      this.page = { ...this.page, items: [...this.mobileRows] };
      changed = true;
    }
    if (changed) this.changed();
  };
  /** D1: una vista guardada con «Última conversación» muestra «Creado» en su lugar. */
  reportColumns = async (columns: string[], automatic = false) => {
    const state = viewState();
    state.autoColumns = automatic;
    state.columns.clear();
    if (!automatic)
      for (const column of columns.map((item) => (item === "LastVisibleConversationActivityAt" ? "CreatedAt" : item)))
        if (contactFields.includes(column)) state.columns.add(column);
    this.persist();
    this.changed();
  };

  // ---- Selección ----
  select(id: number, selected: boolean): void {
    if (selected) viewState().selected.add(id);
    else viewState().selected.delete(id);
    this.persist();
    this.changed();
  }
  clearSelection(): void {
    viewState().selected.clear();
    this.persist();
    this.changed();
  }
  selectPage(selected: boolean): void {
    for (const row of this.page?.items ?? []) {
      if (selected) viewState().selected.add(row.contact.id);
      else viewState().selected.delete(row.contact.id);
    }
    this.persist();
    this.changed();
  }
  saveList = () => {
    viewState().scroll = dom.readScroll();
    this.persist();
  };
  persist(): void {
    this.importWizard?.preservePreparation();
    storeView(viewState().owner);
  }

  // ---- Editor ----
  async saved(id: number): Promise<void> {
    this.persist();
    this.navigate(viewUrl(id));
  }
  navigateFromEditor = async (path: string) => {
    if (!path.startsWith("/") || path.startsWith("//")) return;
    if (this.editor === null || (await this.editor.confirmLeave())) this.navigate(path);
  };

  // ---- Importar ----
  importOpen = () => this.importWizard?.open();
  importPrepared = (id: string | null) => {
    viewState().importBatchId = id;
    this.persist();
  };
  importComplete = async () => {
    viewState().selected.clear();
    await this.load();
  };
  importClosed = () => this.importWizard?.markClosed();

  // ---- Copiar y Exportar ----
  async copy(): Promise<void> {
    const state = viewState();
    if (this.copyBusy || state.selected.size === 0 || this.api.currentRights?.canExport !== true) return;
    const selected = [...state.selected],
      query = state.query,
      owner = state.owner;
    const session = this.api.currentSessionStamp,
      scope = this.api.currentScope;
    this.copyBusy = true;
    this.copied = false;
    this.copyError = null;
    this.copyTextValue = null;
    this.copyPageExpired = false;
    this.changed();
    const authorityChanged = () =>
      owner !== viewState().owner ||
      session !== this.api.currentSessionStamp ||
      scope !== this.api.currentScope ||
      this.api.currentRights?.canExport !== true;
    try {
      this.copyReloadUrl = dom.currentPath() ?? viewUrl();
      const value = clipboardText(
        await this.api.extract({ query, scope: "SELECTED", format: "TSV", selectedIds: selected }, this.pending.signal),
      );
      if (authorityChanged()) return;
      const count = countClipboardRecords(value);
      this.copied = await dom.copyText(value);
      if (authorityChanged()) {
        this.copied = false;
        return;
      }
      if (this.copied) {
        this.copiedCount = count;
        this.copyReceipt++;
      } else {
        this.copyTextValue = value;
        this.copyError = this.L("COPY_FAILED");
      }
    } catch (error) {
      if (!isCancel(error)) {
        this.copyPageExpired = error instanceof ContactRequestFailure && error.code === "CONTACT_PAGE_EXPIRED";
        this.copyError =
          error instanceof ContactRequestFailure && error.code === "INVALID_REQUEST"
            ? this.L("COPY_UNAVAILABLE")
            : this.failure(error);
      }
    } finally {
      this.copyBusy = false;
      this.changed();
    }
  }
  exportOpen = () => {
    this.extractError = null;
    this.changed();
    showDialogFrom(this.elements.extractDialog);
  };
  chooseFormat = (format: string) => {
    this.extractFormat = format;
    this.changed();
  };
  async download(): Promise<void> {
    if (this.extractBusy) return;
    this.extractBusy = true;
    this.extractError = null;
    this.changed();
    const state = viewState();
    const owner = state.owner,
      session = this.api.currentSessionStamp,
      scope = this.api.currentScope;
    try {
      const records = await this.api.extractDownload(
        { query: state.query, scope: "ALL", format: this.extractFormat, selectedIds: null },
        (body) => dom.downloadStream("contactos." + this.extractFormat.toLowerCase(), body),
        this.pending.signal,
      );
      this.elements.extractDialog?.close();
      // Como en Copiar: si la autoridad cambió durante la descarga no se anuncia una entrega vieja.
      if (
        owner !== viewState().owner ||
        session !== this.api.currentSessionStamp ||
        scope !== this.api.currentScope ||
        this.api.currentRights?.canExport !== true
      )
        return;
      this.exportReceipt++;
      this.exportedMessage = formatText(this.L(records === 1 ? "EXPORTED_ONE" : "EXPORTED"), records);
    } catch (error) {
      if (!isCancel(error)) this.extractError = this.failure(error);
    } finally {
      this.extractBusy = false;
      this.changed();
    }
  }
  closeExport = () => this.elements.extractDialog?.close();
  showManualCopy = () => this.elements.copyDialog?.show();

  // ---- Hoja del celular (⋮) ----
  openMobileMenu = () => {
    this.mobileSortOpen = false;
    this.changed();
    showDialogFrom(this.elements.mobileSheet);
  };
  openMobileSort = () => {
    this.sortDraft = normalSort(viewState().query.sort);
    this.mobileSortOpen = true;
    this.sheetFocus = "title";
    this.changed();
  };
  closeMobileSort = () => {
    this.mobileSortOpen = false;
    this.sheetFocus = "entry";
    this.changed();
  };
  /** Elegir otra columna no cierra la hoja y pone su orden de siempre. */
  pickMobileColumn = (column: string) => {
    if (sortColumn(this.sortDraft) !== column) this.sortDraft = column + (isDateColumn(column) ? "_DESC" : "_ASC");
    this.changed();
  };
  pickMobileDirection = (descending: boolean) => {
    this.sortDraft = sortColumn(this.sortDraft) + (descending ? "_DESC" : "_ASC");
    this.changed();
  };
  /** «Aplicar» ordena en el servidor con la dirección, como en escritorio, y cierra la hoja. */
  applyMobileSort = async () => {
    this.elements.mobileSheet?.close();
    this.mobileSortOpen = false;
    this.changed();
    if (this.sortDraft !== normalSort(viewState().query.sort)) await this.order(this.sortDraft);
  };
  mobileImport = () => {
    this.elements.mobileSheet?.close();
    this.mobileSortOpen = false;
    this.changed();
    this.importOpen();
  };
  mobileExport = () => {
    this.elements.mobileSheet?.close();
    this.mobileSortOpen = false;
    this.changed();
    this.exportOpen();
  };
  /** Lo que dice la fila «Ordenar por» de la hoja: columna y orden («Creado · Más reciente»). */
  currentSortLabel = (sort: string) =>
    this.sortFieldLabel(sortColumn(sort)) + " · " + this.orderLabel(sortColumn(sort), sortDescending(sort));

  /** Nombre visible del contacto (Name). */
  name = fullName;
}

/** Consulta sin filtros (new ContactQuery() de .NET): las lecturas internas de revalidación. */
function parseEmpty(): ContactQuery {
  return {
    search: null,
    tags: null,
    anyTag: false,
    inboxId: null,
    agentUserId: null,
    dateField: "CREATED",
    period: "ANY",
    from: null,
    to: null,
    sort: "CREATED",
    page: 1,
    pageSize: 25,
    agentKind: "HUMAN",
    selectedIds: null,
  };
}

/** El portapapeles y la copia manual usan los mismos saltos LF. */
export const clipboardText = (content: string) => content.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

/** Registros TSV efectivos sin confundir saltos dentro de campos entrecomillados con filas. */
export function countClipboardRecords(content: string): number {
  let quoted = false,
    rows = 0;
  for (let i = 0; i < content.length; i++) {
    if (content[i] === '"') {
      if (quoted && i + 1 < content.length && content[i + 1] === '"') {
        i++;
        continue;
      }
      quoted = !quoted;
    } else if (content[i] === "\n" && !quoted) rows++;
  }
  if (content.length > 0 && content[content.length - 1] !== "\n") rows++;
  return Math.max(0, rows - 1);
}

/** show() del portal actual: modal, el foco vuelve a quien lo abrió y un clic fuera del recuadro cierra. */
const showDialogFrom = (dialog: HTMLDialogElement | null) => showDialog(dialog);
